import { useEffect } from 'react';
import { AppProvider, useApp } from '@/context/AppContext';
import AuthGate from '@/screens/auth/AuthGate';
import DesktopLayout from '@/layouts/DesktopLayout';
import MobileLayout from '@/layouts/MobileLayout';
import BidPortal from '@/screens/public/BidPortal';
import InvoicePortal from '@/screens/public/InvoicePortal';
import { supabase } from '@/data/supabase';
import { installAutoFlush } from '@/lib/offlineQueue';

function AppShell() {
  const { isMobile, userRole } = useApp();
  if (userRole === 'employee') return <MobileLayout />;
  return isMobile ? <MobileLayout /> : <DesktopLayout />;
}

// Public routes (/bid/:token, /invoice/:token) bypass AuthGate so unauthenticated
// GCs and customers can use them.
function matchPublicRoute(pathname) {
  const bidMatch = pathname.match(/^\/bid\/([0-9a-f-]{8,})\/?$/i);
  if (bidMatch) return { type: 'bid', token: bidMatch[1] };

  const invoiceMatch = pathname.match(/^\/invoice\/([0-9a-f-]{8,})\/?$/i);
  if (invoiceMatch) return { type: 'invoice', token: invoiceMatch[1] };

  return null;
}

export default function App() {
  const route = matchPublicRoute(
    typeof window !== 'undefined' ? window.location.pathname : '/'
  );

  // Drain queued offline punches on online/focus. Without this the IndexedDB
  // queue grows forever even when the device has connectivity.
  useEffect(() => {
    if (!supabase) return undefined;
    return installAutoFlush(supabase);
  }, []);

  if (route?.type === 'bid')     return <BidPortal token={route.token} />;
  if (route?.type === 'invoice') return <InvoicePortal token={route.token} />;

  return (
    <AuthGate>
      {({ userId, email }) => (
        <AppProvider userId={userId} userEmail={email}>
          <AppShell />
        </AppProvider>
      )}
    </AuthGate>
  );
}
